import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '$partials/ui';
import { IconClose, IconStapleFront } from '$partials/icons';
import { useKeyboard } from '$hooks/events';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GlossarylModal = ({
    isOpen = false,
    onClose = () => {},
    termId
}) => {
    const [animationOut, setAnimationOut] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const dialogRef = useRef();
    const scrollRef = useRef();
    const controller = useRef();

    /**
     * Load content
     * @returns {Promise<object>}
     */
    const load = async () => {
        try {
            (loading && controller.current) && controller.current.abort();

            setLoading(true);

            let signal;
            if (window && window.AbortController) {
                controller.current = new window.AbortController();
                signal = controller.current.signal;
            }

            const response = await fetch(`/api/v1/glossary/${termId}`, { signal });

            if (response) {
                if (response.status !== 200) {
                    setLoading(false);
                    return;
                }

                const data = await response.json();

                setLoading(false);
                setData(data);
            }
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') { return; }
            console.error('[GlossarylModal]', err);
        }
    };

    useEffect(() => {
        if (!termId) { return; }
        load();
        return () => (loading && controller.current) && controller.current.abort();
    }, [termId]);

    const handleClose = () => {
        setAnimationOut(true);
        setTimeout(() => onClose(), 200);
    };

    useKeyboard({ 'Escape': () => isOpen && handleClose() }, [isOpen]);

    useEffect(() => {
        const dialogNode = dialogRef.current;
        if (isOpen) {
            if (dialogNode && !dialogNode.open) {
                dialogNode.showModal?.();
                disableBodyScroll(scrollRef.current);
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(scrollRef.current);
                dialogNode.close?.();
                setAnimationOut(false);
            }
        }

    }, [isOpen, dialogRef.current]);

    useEffect(() => () => clearAllBodyScrollLocks(), []);

    return (
        <dialog
            className={`dialog-reset fade-in backdrop-fade-in ${animationOut ? 'fade-out' : ''}`}
            onClick={handleClose}
            ref={dialogRef}>
            <div
                className='relative max-h-screen'
                ref={scrollRef}>
                {data &&
                    <article className={`fade-in w-full fixed top-0 bottom-0 left-0 max-h-screen slide-x-in ${animationOut ? 'slide-out' : ''}`}>
                        <div
                            className='relative bg-yellow w-full md:w-6/12 min-h-screen md:min-h-[unset] p-20 sm:p-40 md:pt-50 md:pb-60 md:px-60'
                            onClick={(e) => e.stopPropagation()}>
                            <div className='relative flex flex-wrap'>
                                <div className='w-full md:w-2/12 mb-30'>
                                    <IconStapleFront />
                                </div>

                                <div className='w-full sm:w-8/12'>
                                    <header className='mb-40 md:mt-[42px]'>
                                        <p className='m-0 mb-30'>
                                            {data?.index &&
                                                <span className='mr-60'>
                                                    {String(data.index).padStart(Math.max(String(data.index).length, 2), '0')}
                                                </span>
                                            }
                                            <span>
                                                Glossary
                                            </span>
                                        </p>

                                        <h2 className='type-style-3 mb-30'>
                                            {data?.term}
                                        </h2>
                                    </header>

                                    {data.body &&
                                        <section
                                            className='rich-text mb-40'
                                            dangerouslySetInnerHTML={{ __html: data.body }} />
                                    }

                                    <div className='flex flex-wrap'>
                                        {data.glossaryPage?.url &&
                                            <Button
                                                as='a'
                                                className='pill-button cursor-pointer mr-10 mb-10'
                                                href={data.glossaryPage.url}>
                                                Full Glossary
                                            </Button>
                                        }

                                        {data.learnMorePage?.url &&
                                            <Button
                                                as='a'
                                                className='pill-button cursor-pointer mb-10'
                                                href={data.learnMorePage.url}>
                                                Learn more
                                            </Button>
                                        }
                                    </div>
                                </div>
                                <Button
                                    className='absolute right-0 top-0'
                                    onClick={handleClose}
                                    renderIcon={<IconClose />}>
                                </Button>
                            </div>
                        </div>
                    </article>
                }
            </div>
        </dialog>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

GlossarylModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    termId: PropTypes.string.isRequired
};

export default GlossarylModal;
